import { Component, Input, OnInit } from "@angular/core";
import { Helper } from "src/app/core/helpers/helper";
import { ChatService } from "../../services/chat.service";
import { Subscription } from "rxjs";
import { Lightbox } from "ngx-lightbox";
import { AttachmentsType, ChatType } from "src/app/core/constants/constants";

@Component({
  selector: "app-chat",
  templateUrl: "./chat.component.html",
  styleUrl: "./chat.component.scss",
  standalone: false
})
export class ChatComponent implements OnInit {
  @Input() bookingDetails: any;
  @Input() chatType!: ChatType;
  groupedChatList: Array<any> = [];
  readonly AttachmentsType = AttachmentsType;
  private chatSubscription!: Subscription;

  constructor(public helper: Helper, private chatService: ChatService, private lightbox: Lightbox) {}

  /**
   * Resolves what a message actually is, so the template can render it instead of printing a
   * storage path as if it were something the rider typed.
   *
   * `messageType` is authoritative — the server stores it on every message and the fetch handler
   * returns whole message objects, so it arrives intact. The extension fallback covers messages
   * written before that field was populated: those carry `TEXT` (the schema default) while the body
   * is really a `chat_document/…` path, which is how an uploaded photo showed up as link text.
   */
  attachmentType(data: any): AttachmentsType {
    const message: string = data?.message ?? "";
    const declared: AttachmentsType | undefined = data?.messageType;

    if (declared && declared !== AttachmentsType.TEXT) return declared;
    if (!message.startsWith("chat_document/")) return AttachmentsType.TEXT;

    if (/\.(jpg|jpeg|png|gif|webp|bmp|heic)$/i.test(message)) return AttachmentsType.IMAGE;
    if (/\.(mp4|mov|webm|m4v|avi)$/i.test(message)) return AttachmentsType.VIDEO;
    if (/\.(mp3|wav|ogg|m4a|aac)$/i.test(message)) return AttachmentsType.AUDIO;
    return AttachmentsType.DOCUMENT;
  }

  /** Absolute URL for an attachment stored as a bucket-relative path. */
  attachmentUrl(message: string): string {
    return this.helper.imageUrl + message;
  }

  /** Display name for a non-previewable attachment (uploads carry a generated prefix). */
  attachmentName(message: string): string {
    return (message ?? "").split("/").pop() || message;
  }

  /** Opens an image attachment full size, same lightbox the admin panel chat uses. */
  openImageLightBox(image: string): void {
    this.lightbox.open([{ src: image, caption: "", thumb: "" }], 0, {});
  }

  /**
   * Sets the scroll properties for the chat component.
   * Adjusts the height and overflow properties of the chat scroll container.
   * Also sets the position of the chat header date to sticky.
   */
  setChatScroll(): void {
    const headerHeight = document.getElementById("booking-details-modal-header")?.clientHeight ?? 0;
    const tabHeight = document.getElementById("tab-height")?.clientHeight ?? 0;
    const windowHeight = window.innerHeight; // Get the viewport height

    const maxHeight = windowHeight - headerHeight - tabHeight - 2; // Adjust according to your needs

    const settingsScroll = document.querySelector(".chat-scroll") as HTMLElement;
    if (settingsScroll) {
      settingsScroll.style.height = `${maxHeight}px`;
      settingsScroll.style.overflowY = "auto";
      settingsScroll.style.overflowX = "hidden";
      settingsScroll.style.scrollBehavior = "smooth";
    }

    setTimeout(() => {
      const chatHeaderDate = document.getElementById("chat-header-date") as HTMLElement;
      if (chatHeaderDate) {
        chatHeaderDate.style.position = "sticky";
        chatHeaderDate.style.top = "24px";
      }
    }, 100);
  }

  /**
   * Initializes the chat component.
   * Subscribes to chat messages and groups them by date.
   * Sets the chat scroll properties after grouping the chat messages.
   */
  ngOnInit(): void {
    this.chatSubscription = this.chatService.chatMessages$.subscribe((chatList) => {
      if (chatList?.length > 0) {
        this.groupedChatList = [];
        this.groupedChatList = this.groupChatsByDate(chatList);
        if (this.groupedChatList.length > 0) {
          setTimeout(() => {
            this.setChatScroll();
          }, 100);
        }
      } else {
        this.groupedChatList = [];
      }
    });
  }

  /**
   * Groups chat messages by date.
   * Adds customer and driver details to the chat messages based on their type.
   *
   * @param chatList The list of chat messages to be grouped.
   * @returns An array of grouped chat messages with date headers.
   */
  groupChatsByDate(chatList: any[]): any[] {
    const groupedChats: Array<any> = [];
    let currentDate: any = null;

    chatList.forEach((chat) => {
      if (chat.type === this.helper.UserType.CUSTOMER && chat.typeId === this.bookingDetails?.customerDetail?.id) {
        chat.customerDetail = this.bookingDetails.customerDetail;
      }
      if (chat.type === this.helper.UserType.DRIVER && chat.typeId === this.bookingDetails?.confirmedDriver?.id) {
        chat.confirmedDriver = this.bookingDetails.confirmedDriver;
      }
      if (chat.type === this.helper.UserType.MERCHANT && chat.typeId === this.bookingDetails?.quickCommerce?.merchantDetail?.id) {
        chat.merchantDetail = this.bookingDetails.quickCommerce.merchantDetail;
      }

      const chatDate = this.helper.moment(chat.createdAt).tz(this.helper.displayTimezone).format("YYYY-MM-DD");

      if (chatDate !== currentDate) {
        currentDate = chatDate;
        groupedChats.push({
          isDateHeader: true,
          date: currentDate
        });
      }
      groupedChats.push(chat);
    });

    return groupedChats;
  }

  /**
   * Handles the error event for customer images.
   * Sets the default user image if the customer image fails to load.
   *
   * @param event The error event triggered by the image element.
   * @param data The chat message data containing customer details.
   */
  handleCustomerImageError(event: any, data: any): void {
    event.target.src = this.helper.DefaultImages.DefaultUserImage;
    if (data.customerDetail) {
      data.customerDetail.imageUrl = "";
    }
  }

  /**
   * Handles the error event for driver images.
   * Sets the default user image if the driver image fails to load.
   *
   * @param event The error event triggered by the image element.
   * @param data The chat message data containing driver details.
   */
  handleDriverImageError(event: any, data: any): void {
    event.target.src = this.helper.DefaultImages.DefaultUserImage;
    if (data.confirmedDriver) {
      data.confirmedDriver.imageUrl = "";
    }
  }

  handleMerchantImageError(event: any, data: any): void {
    event.target.src = this.helper.DefaultImages.DefaultUserImage;
    if (data.merchantDetail) {
      data.merchantDetail.imageUrl = "";
    }
  }

  ngOnDestroy(): void {
    this.chatSubscription?.unsubscribe();
  }
}
